import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule, Optional, SkipSelf } from '@angular/core';
import { Level, PatternLayout, BrowserConsoleAppender, getRootLogger, getLogger, Appender, JsonLayout } from 'log4javascript';
import * as i1 from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { assign } from 'lodash';
import { Subject, interval, fromEvent } from 'rxjs';
import { startWith, switchMapTo } from 'rxjs/operators';

/**
 * Token used by the [Logging Module]{@link ../modules/LoggingModule.html}
 * to configure the [Logging Service]{@link ../injectables/LoggingService.html}
 */
const LOGGING_SERVICE_CONFIG = new InjectionToken('sdpp-web-logging-service-config');

var AppenderType;
(function (AppenderType) {
    AppenderType["CONSOLE"] = "CONSOLE";
    AppenderType["HTTP"] = "HTTP";
})(AppenderType || (AppenderType = {}));

var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["ALL"] = 0] = "ALL";
    LogLevel[LogLevel["TRACE"] = 1] = "TRACE";
    LogLevel[LogLevel["DEBUG"] = 2] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 3] = "INFO";
    LogLevel[LogLevel["WARN"] = 4] = "WARN";
    LogLevel[LogLevel["ERROR"] = 5] = "ERROR";
    LogLevel[LogLevel["FATAL"] = 6] = "FATAL";
    LogLevel[LogLevel["OFF"] = 7] = "OFF";
})(LogLevel || (LogLevel = {}));
var LogLevelHelper;
(function (LogLevelHelper) {
    const stringKeys = new Map([
        ['all', LogLevel.ALL],
        ['trace', LogLevel.TRACE],
        ['debug', LogLevel.DEBUG],
        ['info', LogLevel.INFO],
        ['warn', LogLevel.WARN],
        ['error', LogLevel.ERROR],
        ['fatal', LogLevel.FATAL],
        ['off', LogLevel.OFF]
    ]);
    const translationKeys = new Map([
        [LogLevel.ALL, Level.ALL],
        [LogLevel.TRACE, Level.TRACE],
        [LogLevel.DEBUG, Level.DEBUG],
        [LogLevel.INFO, Level.INFO],
        [LogLevel.WARN, Level.WARN],
        [LogLevel.ERROR, Level.ERROR],
        [LogLevel.FATAL, Level.FATAL],
        [LogLevel.OFF, Level.OFF]
    ]);
    /**
     * Transforms a LogLevel object (used by LoggingService) into a Level object (
     * used by log4javascript).
     */
    LogLevelHelper.getLevel = (value) => translationKeys.get(value);
    /**
     * Parses the string value to return a LogLevel enum value.
     * @param value The string value.
     */
    LogLevelHelper.from = (value) => stringKeys.get(value.toLowerCase());
})(LogLevelHelper || (LogLevelHelper = {}));

// third party
/**
 * [Layout]{@link http://log4javascript.org/docs/manual.html#layouts} used when not
 * configuration is applied to the Logging Service.
 */
const DEFAULT_LAYOUT = new PatternLayout('%d{HH:mm:ss} %-5p [%c] %m%n');
/**
 * Wrapper for the
 * [BrowserConsoleAppender]{@link http://log4javascript.org/docs/manual.html#browserconsoleappender} from log4javascript
 */
class ConsoleAppender extends BrowserConsoleAppender {
    /**
     * Generate an instance of the BrowserConsoleAppender wrapper
     *
     * @param  id Id of the appender
     */
    constructor(config) {
        super();
        this.config = config;
        this.init();
    }
    /**
     * Returns the id of the appender
     */
    toString() {
        return this.config.id;
    }
    init() {
        // set threshold
        this.setThreshold(LogLevelHelper.getLevel(this.config.level));
        // set layout
        const layout = this.config.pattern ? new PatternLayout(this.config.pattern) : DEFAULT_LAYOUT;
        this.setLayout(layout);
    }
}

/**
 * Wrapper implementation of [log4javascript](http://log4javascript.org) logger.
 */
const ROOT_LOGGER_NAME = 'root';
/* eslint-disable @typescript-eslint/no-explicit-any */
class LoggerProxy {
    /**
     * Create a Logger4js instance, assign the level and the appenders.
     *
     * @param name LoggerId
     * @param level Level assigned
     * @param appenders List of appenders to add to the logger.
     */
    constructor(name, level, appenders) {
        this.logger = name === ROOT_LOGGER_NAME ? getRootLogger() : getLogger(name);
        if (level != null) {
            this.setLevel(level);
        }
        if (appenders && appenders.length > 0) {
            appenders.forEach((appender) => {
                this.logger.addAppender(appender);
            });
        }
    }
    /**
     * The name of the logger.
     */
    get name() {
        return this.logger.name;
    }
    /**
     * Sets the level for the Logger
     * @param level Level assigned
     */
    setLevel(level) {
        this.logger.setLevel(LogLevelHelper.getLevel(level));
    }
    /**
     * Publish a message in trace level
     *
     * @param args Values to publish
     */
    trace(...args) {
        this.logger.trace(this.transform(args));
    }
    /**
     * Publish a message in debug level
     *
     * @param args Values to publish
     */
    debug(...args) {
        this.logger.debug(this.transform(args));
    }
    /**
     * Publish a message in info level
     *
     * @param args Values to publish
     */
    info(...args) {
        this.logger.info(this.transform(args));
    }
    /**
     * Publish a message in warning level
     *
     * @param args Values to publish
     */
    warn(...args) {
        this.logger.warn(this.transform(args));
    }
    /**
     * Publish a message in error level
     *
     * @param args Values to publish
     */
    error(...args) {
        this.logger.error(this.transform(args));
    }
    /**
     * Publish a message in fatal level
     *
     * @param args Values to publish
     */
    fatal(...args) {
        this.logger.fatal(this.transform(args));
    }
    transform(args) {
        return args.map((value) => (typeof value === 'object' ? JSON.stringify(value) : value));
    }
}

const DEFAULT_BATCH_SIZE = 100;
const DEFAULT_INTERVAL_MS = 30000;
const DEFAULT_PATTERN = `%d{yyyy-MM-dd'T'HH:mm:ss.SSSZ} %-5p [%c] %m`;
const APPLICATION_CODE = 'applicationCode';
class HttpAppenderFactory {
    constructor(handler) {
        this.handler = handler;
    }
    /**
     * Generate an instance of the HttpAppender
     *
     * @param config config of the appender
     */
    getAppender(config) {
        return new HttpAppender(this.handler, config);
    }
}
/** @nocollapse */ /** @nocollapse */ HttpAppenderFactory.ɵfac = function HttpAppenderFactory_Factory(t) { return new (t || HttpAppenderFactory)(i0.ɵɵinject(i1.HttpBackend)); };
/** @nocollapse */ /** @nocollapse */ HttpAppenderFactory.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: HttpAppenderFactory, factory: HttpAppenderFactory.ɵfac, providedIn: 'root' });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HttpAppenderFactory, [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], function () { return [{ type: i1.HttpBackend }]; }, null);
})();
/**
 * HTTPAppender sends logs to the back-end
 */
class HttpAppender extends Appender {
    constructor(handler, config) {
        super();
        this.config = config;
        this._batchSent = new Subject();
        this._batch = [];
        // new HttpClient(handler) allows to bypass Telemetry interceptor
        // see  https://github.com/angular/angular/issues/20203#issuecomment-368680437
        this._http = new HttpClient(handler);
        this.init();
    }
    /**
     * Returns the id of the appender
     */
    toString() {
        return this.config.id;
    }
    append(loggingEvent) {
        this._batch.push(loggingEvent);
        if (this._batch.length >= this._batchSize) {
            this.sendBatch();
        }
    }
    sendBatch() {
        if (this._batch.length > 0) {
            const json = this.formatBatch(this._batch);
            this._http
                .post(this.config.url, json, {
                headers: { 'content-type': 'application/json' }
            })
                .subscribe({
                error: error => {
                    // eslint-disable-next-line no-console
                    console.error('Could not send logs', error);
                }
            });
            this._batch = [];
            this._batchSent.next();
        }
    }
    /**
     * SendOnUnload is not reliable, we do our best.
     */
    sendOnUnload() {
        if (navigator.sendBeacon && this._batch.length > 0) {
            // sendBeacon with application/json works on Firefox but is blocked on Chrome
            // TODO ask back-end to accept text/plain for Chrome
            navigator.sendBeacon(this.config.url, new Blob([this.formatBatch(this._batch)], { type: 'application/json' }));
            this._batch = [];
        }
        else {
            // this won't work on most browsers
            this.sendBatch();
        }
    }
    formatBatch(batch) {
        const formattedMessages = batch.map(event => this.getLayout().formatWithException(event));
        return `[${formattedMessages.join(', ')}]`;
    }
    init() {
        // set batchSize & intervalMs
        this._batchSize = this.config.batchSize != null ? this.config.batchSize : DEFAULT_BATCH_SIZE;
        this._intervalMs = this.config.intervalMs != null ? this.config.intervalMs : DEFAULT_INTERVAL_MS;
        // set threshold
        this.setThreshold(LogLevelHelper.getLevel(this.config.level));
        // set layout
        const layout = new JsonPatternLayout(this.config.pattern);
        layout.setCustomField(APPLICATION_CODE, this.config.applicationCode);
        this.setLayout(layout);
        // schedule regular sendings
        this._batchSent.pipe(startWith(null), switchMapTo(interval(this._intervalMs))).subscribe(() => {
            this.sendBatch();
        });
        // listen to window unload
        fromEvent(window, 'beforeunload').subscribe(() => {
            this.sendOnUnload();
        });
    }
}
class JsonPatternLayout extends JsonLayout {
    constructor(pattern = DEFAULT_PATTERN) {
        super(false, false);
        this._messageFormatter = new PatternLayout(pattern);
    }
    format(loggingEvent) {
        let jsonEvent = super.format(loggingEvent.messages.reduce((result, message, _index, _messages) => {
            result.messages.push(this._messageFormatter.format(assign({}, loggingEvent, { messages: [message] })));
            return result;
        }, assign({}, loggingEvent, { messages: [] })));
        /* Sometimes the message contains some inner stringified JSON that messes up the messageFormatter output.
         * We need to check the generated output is proper JSON and if not we return a specific message for the event.
         */
        if (!this.isJson(jsonEvent)) {
            jsonEvent = super.format(assign({}, loggingEvent, {
                messages: [
                    `The original message could not be printed as JSON. ` +
                        `This may happen if the message itself contains some stringified JSON.`
                ]
            }));
        }
        return jsonEvent;
    }
    isJson(value) {
        try {
            JSON.parse(value);
        }
        catch (_a) {
            return false;
        }
        return true;
    }
}

/**
 * Wrapper of the library [log4javascript]{@link http://log4javascript.org} for angular.
 */
class LoggingService {
    /**
     * Apply the configuration when is defined. In the other case apply the default
     * configuration.
     *
     * @param config Configuration for the RootLogger and other loggers.
     */
    constructor(_httpAppenderFactory, config) {
        this._httpAppenderFactory = _httpAppenderFactory;
        this.loggers = new Map();
        this.configs = new Map();
        if (config) {
            this.initConfiguration(config);
        }
        else {
            this.initRootConfiguration();
        }
    }
    /**
     * Get a logger using the loggerId.
     * If the logger doesn't exist, it will create one and apply the configuration assigned
     * to that logger. If there isn't a configuration for the logger it will use the
     * RootLogger configuration.
     *
     * @param loggerId loggerId.
     */
    getLogger(loggerId) {
        if (!this.loggers.has(loggerId)) {
            let logger;
            if (this.configs.has(loggerId)) {
                const configuration = this.configs.get(loggerId);
                const appenders = configuration.appenders.map((appender) => this.getAppender(appender));
                logger = new LoggerProxy(loggerId, configuration.level, appenders);
            }
            else {
                logger = new LoggerProxy(loggerId);
            }
            this.loggers.set(loggerId, logger);
        }
        return this.loggers.get(loggerId);
    }
    /**
     * Gets the root logger.
     */
    getRootLogger() {
        return this.rootLogger;
    }
    /**
     * Gets all the children loggers.
     */
    getAllChildrenLoggers() {
        return Array.from(this.loggers.values());
    }
    initConfiguration(config) {
        const rootConfig = config.root;
        if (rootConfig) {
            this.initRootConfiguration(rootConfig);
        }
        const childrenConfig = config.children;
        if (childrenConfig && childrenConfig.length !== 0) {
            this.initChildrenConfiguration(childrenConfig);
        }
    }
    initRootConfiguration(rootConfig) {
        const rootAppenders = [];
        if (rootConfig && rootConfig.appenders) {
            for (const rootAppender of rootConfig.appenders) {
                const appender = this.getAppender(rootAppender);
                if (appender !== null) {
                    rootAppenders.push(appender);
                }
            }
        }
        else {
            rootAppenders.push(this.getDefaultConsoleAppender());
        }
        this.rootLogger = new LoggerProxy(ROOT_LOGGER_NAME, rootConfig.level, rootAppenders);
    }
    initChildrenConfiguration(childrenConfiguration) {
        childrenConfiguration.forEach((configuration) => {
            this.configs.set(configuration.id, configuration);
        });
    }
    getAppender(configuration) {
        switch (configuration.type) {
            case AppenderType.CONSOLE:
                return this.getConsoleAppender(configuration);
            case AppenderType.HTTP:
                return this.getHttpAppender(configuration);
            default:
                return null;
        }
    }
    getConsoleAppender(configuration) {
        return new ConsoleAppender(configuration);
    }
    getDefaultConsoleAppender() {
        return this.getConsoleAppender({
            id: 'Root',
            level: LogLevel.DEBUG,
            type: AppenderType.CONSOLE
        });
    }
    getHttpAppender(configuration) {
        return this._httpAppenderFactory.getAppender(configuration);
    }
}
/** @nocollapse */ /** @nocollapse */ LoggingService.ɵfac = function LoggingService_Factory(t) { return new (t || LoggingService)(i0.ɵɵinject(HttpAppenderFactory), i0.ɵɵinject(LOGGING_SERVICE_CONFIG)); };
/** @nocollapse */ /** @nocollapse */ LoggingService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: LoggingService, factory: LoggingService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LoggingService, [{
            type: Injectable
        }], function () {
        return [{ type: HttpAppenderFactory }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [LOGGING_SERVICE_CONFIG]
                    }] }];
    }, null);
})();

class LoggingModule {
    constructor(injector, parentModule) {
        if (parentModule) {
            throw new Error('LoggingModule is already loaded. Import it in the AppModule only');
        }
        else {
            LoggingModule.injector = injector;
        }
    }
    static getInjector() {
        return LoggingModule.injector;
    }
    static forRoot(config) {
        return {
            ngModule: LoggingModule,
            providers: [LoggingService, { provide: LOGGING_SERVICE_CONFIG, useValue: config }]
        };
    }
}
LoggingModule.injector = null;
/** @nocollapse */ /** @nocollapse */ LoggingModule.ɵfac = function LoggingModule_Factory(t) { return new (t || LoggingModule)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(LoggingModule, 12)); };
/** @nocollapse */ /** @nocollapse */ LoggingModule.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: LoggingModule });
/** @nocollapse */ /** @nocollapse */ LoggingModule.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({});
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LoggingModule, [{
            type: NgModule
        }], function () {
        return [{ type: i0.Injector }, { type: LoggingModule, decorators: [{
                        type: Optional
                    }, {
                        type: SkipSelf
                    }] }];
    }, null);
})();

/**
 * This decorator allows to measure the time of execution for a method.
 *
 * To know about the information published see [MethodMetric]{@link ../interfaces/MethodMetric.html}.
 *
 * ## Example:
 * Measure the time of execution every time that myMethod is called.
 *
 * ```ts
 * export class AnyService {
 *    \@InjectLogger() private logger: Logger;
 *    ...
 * }
 * ```
 *
 * @param loggerId loggerId to be injected.
 *
 */
function InjectLogger(loggerId) {
    return function (target, key) {
        Object.defineProperty(target, key, {
            configurable: false,
            get: () => {
                if (!LoggingModule.getInjector()) {
                    throw new Error('LoggingModule is not loaded. Import it in the AppModule');
                }
                return LoggingModule.getInjector()
                    .get(LoggingService)
                    .getLogger(loggerId ? loggerId : target.constructor.name);
            }
        });
    };
}

/**
 * Generated bundle index. Do not edit.
 */

export { InjectLogger, LOGGING_SERVICE_CONFIG, LogLevel, LogLevelHelper, LoggingModule, LoggingService };
